/**
 * savoirCareStages.js — SAVOIR CARE ED Patient Flow Board stage enum + ordering.
 *
 * What: Canonical stage values, display labels, per-stage staffing roles, column
 *       ordering, and the Patient-Elected Redirect trigger rules for the ED Kanban
 *       board.
 * Why:  A single source of truth so the board, cards, hook, and Cloud Functions
 *       all agree on stage strings. Firestore stores the raw enum value in the
 *       `savoir_care_stage` field (snake_case — invariant).
 * Invariants:
 *   - All Firestore stage values are snake_case.
 *   - `expected` is the left-most pre-arrival CareVerify column (PROMPT 8). Both
 *     urgent and elective bookings land here; ONLY urgent visitType may advance
 *     into incoming/clinical stages (enforced in useSavoirCareBoard).
 *   - `patient_elected_redirect` is a TERMINAL board state: cards in it are never
 *     draggable and cannot advance out.
 *   - Redirect may only be entered from stages 2–4 (trigger rules):
 *     diagnostics_ordered, interventions, initial_review. Triage (pre-assessment),
 *     final_review / dispo_decision / complete, and incoming/expected (not yet
 *     assessed) are NOT redirect-eligible.
 *   - The physician-bookend model places attending-physician judgment at triage
 *     (front bookend) and final_review + dispo_decision (back bookend); APPs run
 *     the execution-oriented middle stages. `incoming` is a pre-arrival,
 *     non-clinical front-desk activation column — no clinical decision is made there
 *     and CareStart never replaces the physician triage step.
 *
 * Ported from HealthDesk Experimental (Exp-B) apps/unified-app/src/features/
 * savoir-care/constants/savoirCareStages.js — pure constants, no adaptation.
 * Tightest coupling: SavoirCareBoardPage / SavoirCareBoardColumn / SavoirCarePatientCard.
 */

/** Stage enum. Values are the snake_case strings persisted to Firestore. */
export const SAVOIR_CARE_STAGES = {
  EXPECTED: 'expected',
  INCOMING: 'incoming',
  TRIAGE: 'triage',
  DIAGNOSTICS_ORDERED: 'diagnostics_ordered',
  INTERVENTIONS: 'interventions',
  INITIAL_REVIEW: 'initial_review',
  FINAL_REVIEW: 'final_review',
  DISPO_DECISION: 'dispo_decision',
  COMPLETE: 'complete',
  PATIENT_ELECTED_REDIRECT: 'patient_elected_redirect',
};

/** Human-readable column / chip labels keyed by stage value. */
export const SAVOIR_CARE_STAGE_LABELS = {
  expected: 'Expected',
  incoming: 'Incoming',
  triage: '1 · Triage',
  diagnostics_ordered: '2 · Diagnostics',
  interventions: '3 · Interventions',
  initial_review: '4 · Initial review',
  final_review: '5 · Final review',
  dispo_decision: '6 · Dispo decision',
  complete: '7 · Complete',
  patient_elected_redirect: 'Patient-elected redirect',
};

/**
 * Staffing role responsible for each stage (physician-bookend model).
 * Drives the per-column role badge. NOT an access guard — write access is gated
 * by role in the hook (canWrite) and enforced server-side by Firestore rules.
 *   - 'physician'  : attending-physician bookend (front-door triage, back-end review/dispo)
 *   - 'app'        : Advanced Practice Provider runs the execution-oriented middle
 *   - 'front_desk' : pre-arrival activation only (no clinical decision)
 *   - 'any'        : handoff/complete
 *   - 'documented' : terminal documented patient choice (redirect)
 */
export const SAVOIR_CARE_STAGE_ROLE = {
  expected: 'front_desk',
  incoming: 'front_desk',
  triage: 'physician',
  diagnostics_ordered: 'app',
  interventions: 'app',
  initial_review: 'app',
  final_review: 'physician',
  dispo_decision: 'physician',
  complete: 'any',
  patient_elected_redirect: 'documented',
};

/** Short, human-readable label for each staffing role (used in the column badge). */
export const SAVOIR_CARE_STAGE_ROLE_LABELS = {
  physician: 'Physician',
  app: 'APP',
  front_desk: 'Front desk',
  any: 'Any',
  documented: 'Documented',
};

/**
 * Clinical columns rendered left-to-right in this exact order. `expected`,
 * `incoming`, and the redirect column are intentionally excluded — they are
 * rendered separately (Expected + Incoming pre-arrival columns on the left, a
 * visually distinct terminal redirect column on the right).
 */
export const CLINICAL_STAGE_ORDER = [
  SAVOIR_CARE_STAGES.TRIAGE,
  SAVOIR_CARE_STAGES.DIAGNOSTICS_ORDERED,
  SAVOIR_CARE_STAGES.INTERVENTIONS,
  SAVOIR_CARE_STAGES.INITIAL_REVIEW,
  SAVOIR_CARE_STAGES.FINAL_REVIEW,
  SAVOIR_CARE_STAGES.DISPO_DECISION,
  SAVOIR_CARE_STAGES.COMPLETE,
];

/**
 * Stages from which a patient may be moved to `patient_elected_redirect`.
 * INVARIANT: Only stages 2–4 are eligible. A patient in triage has not completed
 * assessment (that would be LWBS, a separate future feature — do not conflate),
 * incoming/expected patients are not yet assessed, and a patient past
 * initial_review is too far along clinically to redirect.
 */
export const REDIRECT_ELIGIBLE_STAGES = [
  SAVOIR_CARE_STAGES.DIAGNOSTICS_ORDERED,
  SAVOIR_CARE_STAGES.INTERVENTIONS,
  SAVOIR_CARE_STAGES.INITIAL_REVIEW,
];

/**
 * Stages that require a physician to advance/own (front + back bookends).
 * Enforced as a UI affordance here; durable enforcement is via role gating in the
 * hook and Firestore rules.
 */
export const PHYSICIAN_REQUIRED_STAGES = [
  SAVOIR_CARE_STAGES.TRIAGE,
  SAVOIR_CARE_STAGES.FINAL_REVIEW,
  SAVOIR_CARE_STAGES.DISPO_DECISION,
];

/**
 * The single stage at which an APP may raise the escalation flag to the attending
 * physician. This is the highest-risk APP-only step before final review.
 */
export const ESCALATION_STAGE = SAVOIR_CARE_STAGES.INITIAL_REVIEW;

/**
 * Returns true when a card in the given stage may be moved to the
 * Patient-Elected Redirect column.
 * @param {string} stage current `savoir_care_stage` value
 * @returns {boolean}
 */
export const isRedirectEligible = (stage) =>
  REDIRECT_ELIGIBLE_STAGES.includes(stage);

/**
 * Returns true when an Expected-column card may advance into clinical flow.
 * ONLY urgent CareVerify visitType may leave Expected.
 * @param {object|null|undefined} entry board entry
 * @returns {boolean}
 */
export const canAdvanceFromExpected = (entry) =>
  entry?.savoir_care_stage === SAVOIR_CARE_STAGES.EXPECTED &&
  entry?.visitType === 'urgent';

/**
 * Returns the next forward stage after the given one for the card "Advance" button
 * (the primary movement path), or null when no forward step exists.
 *   - `expected` advances to `incoming` (CareVerify pre-arrival → front-desk lane).
 *     ONLY urgent visitType may take this step (enforced in the board hook).
 *   - `incoming` advances to `triage` (front-desk activation hands the patient to
 *     the front-door physician). `incoming` is deliberately NOT in CLINICAL_STAGE_ORDER.
 *   - clinical stages advance to the next clinical stage; `complete` has no next.
 *   - terminal `patient_elected_redirect` never advances (returns null).
 * @param {string} stage current `savoir_care_stage` value
 * @returns {string|null} next stage value, or null when no forward step exists
 */
export const getNextClinicalStage = (stage) => {
  if (stage === SAVOIR_CARE_STAGES.EXPECTED) return SAVOIR_CARE_STAGES.INCOMING;
  if (stage === SAVOIR_CARE_STAGES.INCOMING) return SAVOIR_CARE_STAGES.TRIAGE;
  const idx = CLINICAL_STAGE_ORDER.indexOf(stage);
  if (idx === -1 || idx === CLINICAL_STAGE_ORDER.length - 1) return null;
  return CLINICAL_STAGE_ORDER[idx + 1];
};

const savoirCareStages = {
  SAVOIR_CARE_STAGES,
  SAVOIR_CARE_STAGE_LABELS,
  SAVOIR_CARE_STAGE_ROLE,
  SAVOIR_CARE_STAGE_ROLE_LABELS,
  CLINICAL_STAGE_ORDER,
  REDIRECT_ELIGIBLE_STAGES,
  PHYSICIAN_REQUIRED_STAGES,
  ESCALATION_STAGE,
  isRedirectEligible,
  canAdvanceFromExpected,
  getNextClinicalStage,
};

export default savoirCareStages;
